import { CalendarEventVisit, Visit, VisitWorkareaStatusRecord, CrewMember, Timesheet, DayViewEvent, ConsultationCalendarEvent, TaskCalendarEvent } from "../scheduleApi"
import { UNASSIGNED_CREW_ID } from "../scheduleModel"
import DayViewCrewLeadSection from "./DayViewCrewLeadSection";
import dayjs from "dayjs";

type Props = {
  events: DayViewEvent[];
  consultations: ConsultationCalendarEvent[];
  tasks?: TaskCalendarEvent[];
  visits: Visit[];
  calendarEventVisits: CalendarEventVisit[];
  visitWorkareaStatuses: VisitWorkareaStatusRecord[];
  timesheets: Timesheet[];
  hoveredEventId: number | null;
  hoveredEventIds: number[];
  setHoveredEventId: (id: number | null) => void;
  setHoveredEventIds: (ids: number[]) => void;
  onConsultationClick?: (consultation: ConsultationCalendarEvent) => void;
  onTaskClick?: (event: TaskCalendarEvent) => void;
  crew: CrewMember[];
  date: dayjs.Dayjs;
  onEnterReorderMode: (selectedCrewLeadId: number | typeof UNASSIGNED_CREW_ID) => void;
}

type CrewLeadGroup = {
  crewLead: CrewMember | null;
  crewLeadId: number | typeof UNASSIGNED_CREW_ID;
  events: DayViewEvent[];
  consultations: ConsultationCalendarEvent[];
  tasks: TaskCalendarEvent[];
}

const groupByCrewLead = (events: DayViewEvent[], consultations: ConsultationCalendarEvent[], tasks: TaskCalendarEvent[], crew: CrewMember[]): CrewLeadGroup[] => {
  const groupMap = new Map<number | typeof UNASSIGNED_CREW_ID, CrewLeadGroup>();

  events.forEach(event => {
    const crewLeadEmail = event.crewForemanEmail;
    const crewLead = crewLeadEmail
      ? crew.find(c => c.userEmail?.toLowerCase() === crewLeadEmail.toLowerCase()) || null
      : null;
    const key = crewLead?.id || UNASSIGNED_CREW_ID;

    if (!groupMap.has(key)) {
      groupMap.set(key, {
        crewLead,
        crewLeadId: key,
        events: [],
        consultations: [],
        tasks: []
      });
    }

    groupMap.get(key)!.events.push(event);
  });

  consultations.forEach(consultation => {
    const organizerEmail = consultation.organizerEmail;
    const organizer = organizerEmail
      ? crew.find(c => c.userEmail?.toLowerCase() === organizerEmail.toLowerCase()) || null
      : null;
    const key = organizer?.id || UNASSIGNED_CREW_ID;

    if (!groupMap.has(key)) {
      groupMap.set(key, {
        crewLead: organizer,
        crewLeadId: key,
        events: [],
        consultations: [],
        tasks: []
      });
    }

    groupMap.get(key)!.consultations.push(consultation);
  });

  tasks.forEach(task => {
    const crewLeadEmail = task.crewForemanEmail;
    const crewLead = crewLeadEmail
      ? crew.find(c => c.userEmail?.toLowerCase() === crewLeadEmail.toLowerCase()) || null
      : null;
    const key = crewLead?.id || UNASSIGNED_CREW_ID;

    if (!groupMap.has(key)) {
      groupMap.set(key, {
        crewLead,
        crewLeadId: key,
        events: [],
        consultations: [],
        tasks: []
      });
    }

    groupMap.get(key)!.tasks.push(task);
  });

  const groups = Array.from(groupMap.values());

  groups.sort((a, b) => {
    if (a.crewLeadId === UNASSIGNED_CREW_ID) return 1;
    if (b.crewLeadId === UNASSIGNED_CREW_ID) return -1;
    const nameA = a.crewLead?.name || '';
    const nameB = b.crewLead?.name || '';
    return nameA.localeCompare(nameB);
  });

  return groups;
};

const DayViewEventList = ({ events, consultations, tasks = [], visits, calendarEventVisits, visitWorkareaStatuses, timesheets, hoveredEventId, hoveredEventIds, setHoveredEventId, setHoveredEventIds, onConsultationClick, onTaskClick, crew, date, onEnterReorderMode }: Props) => {
  const crewLeadGroups = groupByCrewLead(events, consultations, tasks, crew);

  return (
    <div style={{ paddingTop: '12px' }}>
      {crewLeadGroups.map((group, index) => (
        <DayViewCrewLeadSection
          key={group.crewLeadId}
          crewLead={group.crewLead}
          events={group.events}
          consultations={group.consultations}
          tasks={group.tasks}
          visits={visits}
          calendarEventVisits={calendarEventVisits}
          visitWorkareaStatuses={visitWorkareaStatuses}
          timesheets={timesheets}
          date={date}
          hoveredEventId={hoveredEventId}
          hoveredEventIds={hoveredEventIds}
          setHoveredEventId={setHoveredEventId}
          setHoveredEventIds={setHoveredEventIds}
          onConsultationClick={onConsultationClick}
          onTaskClick={onTaskClick}
          onEnterReorderMode={() => onEnterReorderMode(group.crewLeadId)}
          isFirst={index === 0}
        />
      ))}
    </div>
  );
}

export default DayViewEventList